import { SerializedError, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "redux/store";
import { authService } from "./authAPI";
import { AdminStats, ProfilClient, Statistique, User } from "data/types";
import { tokenStorage } from "utils/storage";

interface AuthState {
  user?: User;
  creator?: User;
  error?: SerializedError;
  status?: string;
  resetPasswordError?: string;
  allClients?: any;
  allOwners?: any;
  allServiceAgents?: any;
  allCreators?: any;
  userStats?: Statistique;
  loading?: "idle" | "pending" | "succeeded" | "failed";
  owner?: ProfilClient;
  searchUser?: User;
  users?: User[];
  stats?: AdminStats;
  totalPages?: number;
  totalCount: number;
}

const initialState: AuthState = {
  totalCount: 0,
  totalPages: 0,
  allServiceAgents: {
    services: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  },
  allClients: {
    clients: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  },
  allOwners: {
    owners: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  },
  allCreators: {
    creators: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  },
};
export const currentUser = createAsyncThunk("current", async () => {
  return await authService.currentUser();
});

export const getUser = createAsyncThunk("getUser", async (id: any) => {
  return await authService.getUserById(id);
});

export const getStats = createAsyncThunk("getStats", async () => {
  return await authService.getStats();
});

export const getAllUsers = createAsyncThunk("getAllUsers", async () => {
  return await authService.getAllUsers();
});

export const searchUser = createAsyncThunk("searchUser", async (data: any) => {
  return await authService.searchUser(data);
});

export const resetPassword = createAsyncThunk("resetPassword", async (data: any) => {
  return await authService.resetPassword(data);
});

export const getAllClients = createAsyncThunk("getAllClients", async () => {
  return await authService.getAllClients();
});

// Statistques
export const getAllClientsStats = createAsyncThunk("getAllClientsStats", async () => {
  return await authService.getAllClientsStats();
});

export const getAllServiceAgentStats = createAsyncThunk("getAllServiceAgentStats", async () => {
  return await authService.getAllServiceAgentStats();
});

export const getAllOwners = createAsyncThunk("getAllOwners", async () => {
  return await authService.getAllOwners();
});

export const disableSendMessages = createAsyncThunk(
  "disableSendMessages",
  async ({ clientId, status }: { clientId: number; status: boolean }) => {
    return await authService.disableSendMessages(clientId, status);
  }
);

export const getOwners = createAsyncThunk("getOwners", async () => {
  return await authService.getOwners();
});

export const getAllOwnerStats = createAsyncThunk("getAllOwnerStats", async () => {
  return await authService.getAllOwnerStats();
});

export const getAllServiceAgents = createAsyncThunk("getAllServiceAgents", async () => {
  return await authService.getAllServiceAgents();
});

export const adminGetAllServiceAgents = createAsyncThunk(
  "adminGetAllServiceAgents",
  async ({ page, searchTerm }: { page: any; searchTerm: any }) => {
    return await authService.adminGetAllServiceAgents(page, searchTerm);
  }
);

export const adminGetAllClients = createAsyncThunk(
  "adminGetAllClients",
  async ({ page, searchTerm }: { page: any; searchTerm: any }) => {
    return await authService.adminGetAllClients(page, searchTerm);
  }
);

export const adminGetAllOwners = createAsyncThunk(
  "adminGetAllOwners",
  async ({ page, searchTerm }: { page: any; searchTerm: any }) => {
    return await authService.adminGetAllOwners(page, searchTerm);
  }
);

export const deleteCreator = createAsyncThunk("deleteCreator", async (id: number) => {
  return await authService.deleteCreator(id);
});

export const editCreator = createAsyncThunk("editCreator", async ({ data, id }: { data: any; id: any }) => {
  return await authService.updateCreator(data, id);
});

export const updatePhoneAvailibilty = createAsyncThunk("updatePhoneAvailibilty", async (data: any) => {
  return await authService.updatePhoneAvailibilty(data);
});

export const adminGetAllCreators = createAsyncThunk(
  "adminGetAllCreators",
  async ({ page, searchTerm }: { page: any; searchTerm: any }) => {
    return await authService.adminGetAllCreators(page, searchTerm);
  }
);

export const deleteUser = createAsyncThunk("deleteUser", async (id: number) => {
  return await authService.deleteUser(id);
});

export const validateClient = createAsyncThunk("validateClient", async ({ data, id }: { data: any; id: any }) => {
  return await authService.validateClient(data, id);
});

export const clientValidation = createAsyncThunk(
  "clientValidation",
  async ({ data, id }: { data: any; id: any }) => {
    return await authService.clientValidation(data, id);
  }
);

export const getOwnerById = createAsyncThunk("getOwnerById", async (id: any) => {
  return await authService.getOwnerById(id);
});

export const sendVerificationEmail = createAsyncThunk("sendVerificationEmail", async (email: any) => {
  return await authService.sendVerificationEmail(email);
});

export const chekVerificationOTP = createAsyncThunk(
  "chekVerificationOTP",
  async ({ email, otp }: { email: any; otp: any }) => {
    return await authService.chekVerificationOTP(email, otp);
  }
);

export const authSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    logOut(state) {
      tokenStorage.removeToken();
      localStorage.removeItem("token"); // Backward compatibility
      state.user = undefined;
    },
    resetLoading(state) {
      state.loading = "idle";
    },
  },
  extraReducers: (builder) => {
    // current
    builder
      .addCase(currentUser.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload;
        } else {
          state.user = undefined;
        }
      })
      .addCase(currentUser.rejected, (state, action) => {
        state.error = action.error;
      });

    builder.addCase(editCreator.fulfilled, (state, action) => {
      state.creator = action.payload;
    });

    builder.addCase(updatePhoneAvailibilty.fulfilled, (state, action) => {
      if (state.user) {
        state.user.showPhoneNumber = action.payload.showPhoneNumber;
      }
    });

    // get user by id
    builder
      .addCase(getUser.fulfilled, (state, action) => {
        state.creator = action.payload;
      })
      .addCase(getOwnerById.fulfilled, (state, action) => {
        state.owner = action.payload;
      });

    // get all users
    builder.addCase(getAllUsers.fulfilled, (state, action) => {
      state.users = action.payload;
    });

    //search user
    builder
      .addCase(searchUser.pending, (state, action) => {
        state.loading = "pending";
      })
      .addCase(searchUser.rejected, (state, action) => {
        state.loading = "failed";
      })
      .addCase(searchUser.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.searchUser = action.payload;
      });

    builder
      .addCase(resetPassword.pending, (state, action) => {
        state.loading = "pending";
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        if (action.payload.status === "OK") {
          state.loading = "succeeded";
        }

        if (action.payload.status === "ERROR") {
          state.loading = "failed";
          state.error = action.payload.error;
        }
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = "failed";
      });

    // get all clients
    builder.addCase(getAllClients.fulfilled, (state, action) => {
      state.allClients = action.payload;
    });

    // client stats per month
    builder.addCase(getAllClientsStats.fulfilled, (state, action) => {
      state.userStats = action.payload;
    });

    // get all owners
    builder.addCase(getAllOwners.fulfilled, (state, action) => {
      state.allOwners = action.payload;
    });

    builder.addCase(getOwners.fulfilled, (state, action) => {
      state.allOwners = action.payload;
    });

    builder.addCase(getAllOwnerStats.fulfilled, (state, action) => {
      state.userStats = action.payload;
    });

    // get all service agents
    builder.addCase(getAllServiceAgents.fulfilled, (state, action) => {
      state.allServiceAgents = action.payload;
    });

    builder
      .addCase(adminGetAllServiceAgents.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllServiceAgents.fulfilled, (state, action) => {
        state.allServiceAgents = {
          services: action.payload.services || [],
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
          currentPage: action.payload.currentPage || 1,
        };
        state.loading = "succeeded";
      })
      .addCase(adminGetAllServiceAgents.rejected, (state, action) => {
        state.error = action.error;
        state.loading = "failed";
      });

    builder
      .addCase(adminGetAllClients.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllClients.fulfilled, (state, action) => {
        state.allClients = {
          clients: action.payload.clients || [],
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
          currentPage: action.payload.currentPage || 1,
        };
        state.loading = "succeeded";
      })
      .addCase(adminGetAllClients.rejected, (state, action) => {
        state.error = action.error;
        state.loading = "failed";
      });

    builder
      .addCase(adminGetAllOwners.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllOwners.fulfilled, (state, action) => {
        state.allOwners = {
          owners: action.payload.owners || [],
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
          currentPage: action.payload.currentPage || 1,
        };
        state.loading = "succeeded";
      })
      .addCase(adminGetAllOwners.rejected, (state, action) => {
        state.error = action.error;
        state.loading = "failed";
      });

    builder.addCase(getAllServiceAgentStats.fulfilled, (state, action) => {
      state.userStats = action.payload;
    });

    // get all creators
    builder
      .addCase(adminGetAllCreators.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(adminGetAllCreators.fulfilled, (state, action) => {
        state.allCreators = {
          creators: action.payload.creators || [],
          totalCount: action.payload.totalCount || 0,
          totalPages: action.payload.totalPages || 0,
          currentPage: action.payload.currentPage || 1,
        };
        state.loading = "succeeded";
      })
      .addCase(adminGetAllCreators.rejected, (state, action) => {
        state.error = action.error;
        state.loading = "failed";
      });
    // validate client
    builder
      .addCase(validateClient.pending, (state, action) => {
        state.loading = "pending";
      })
      .addCase(validateClient.fulfilled, (state, action) => {
        if (action.payload.status === "OK") {
          state.loading = "succeeded";
        }
        if (action.payload.status === "VALIDATION") {
          state.loading = "failed";
          state.error = action.payload.error[0].msg ?? "failed";
        }
        if (action.payload.status === "ERROR") {
          state.loading = "failed";
          state.error = action.payload.error;
        }
      })
      .addCase(validateClient.rejected, (state, action) => {
        state.loading = "failed";
      });

    // send Verification Email
    builder
      .addCase(sendVerificationEmail.pending, (state, action) => {
        state.loading = "pending";
      })
      .addCase(sendVerificationEmail.fulfilled, (state, action) => {
        if (action.payload.status === "OK") {
          state.loading = "succeeded";
        }
        if (action.payload.status === "VALIDATION") {
          state.loading = "failed";
          state.error = action.payload.error[0].msg ?? "failed";
        }
        if (action.payload.status === "ERROR") {
          state.loading = "failed";
          state.error = action.payload.error;
        }
      })
      .addCase(sendVerificationEmail.rejected, (state, action) => {
        state.loading = "failed";
      });

    // chek Verification OTP
    builder
      .addCase(chekVerificationOTP.pending, (state, action) => {
        state.loading = "pending";
      })
      .addCase(chekVerificationOTP.fulfilled, (state, action) => {
        if (action.payload.status === "OK") {
          state.loading = "succeeded";
        }
        if (action.payload.status === "VALIDATION") {
          state.loading = "failed";
          state.error = action.payload.error[0].msg ?? "failed";
        }
        if (action.payload.status === "ERROR") {
          state.loading = "failed";
          state.error = action.payload.error;
        }
      })
      .addCase(chekVerificationOTP.rejected, (state, action) => {
        state.loading = "failed";
      });

    // get stats
    builder.addCase(getStats.fulfilled, (state, action) => {
      state.stats = action.payload;
    });
  },
});

export const { logOut, resetLoading } = authSlice.actions;

export const selectCount = (state: RootState) => state.auth;

export default authSlice.reducer;
